import React, { useState } from 'react';
import axios from 'axios';
import { formatPriceFR, formatRentFR, formatSurfaceFR, formatNumberFR } from '../../utils/formatFR';

const API = process.env.REACT_APP_BACKEND_URL;

const SAMPLE_MANDATE = `MANDAT DE VENTE EXCLUSIF

Vendeur : Monsieur Jean DUPONT, 06 12 34 56 78, jean.dupont@email.com, demeurant 25 boulevard Victor Hugo 06000 Nice.

Objet : Cession du fonds de commerce de RESTAURATION RAPIDE situé au 15 avenue Jean Médecin 06000 Nice.
Surface totale : 85 m² avec terrasse de 12 m².
Loyer mensuel : 3 500 € HT/HC. Charges : 250 €.

Chiffre d'affaires HT 2024 : 420 000 €. EBE retraité 2024 : 90 000 €.

Prix de cession : 280 000 € HT (Honoraires d'agence inclus de 28 000 €).

Mandat n° MDT-2026-042, signé le 15/04/2026, durée 12 mois, exclusivité accordée.`;

const FieldRow = ({ label, value, isImportant }) => {
  if (value === null || value === undefined || value === '') return null;
  const display = typeof value === 'number'
    ? formatNumberFR(value)
    : (typeof value === 'boolean' ? (value ? 'Oui' : 'Non') : value);
  return (
    <div className={`flex justify-between gap-3 py-1.5 ${isImportant ? 'border-b border-emerald-200' : 'border-b border-slate-100'}`}>
      <span className={`text-xs ${isImportant ? 'font-semibold text-emerald-700' : 'text-slate-500'}`}>{label}</span>
      <span className={`text-xs font-medium text-right num ${isImportant ? 'text-emerald-900' : 'text-slate-800'}`}>{display}</span>
    </div>
  );
};

const ImportMandateModal = ({ open, onClose, onApply }) => {
  const [text, setText] = useState('');
  const [extracted, setExtracted] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  if (!open) return null;

  const reset = () => {
    setText('');
    setExtracted(null);
    setError(null);
    setLoading(false);
  };

  const close = () => {
    reset();
    onClose();
  };

  const analyze = async () => {
    if (text.trim().length < 50) {
      setError('Texte trop court (min 50 caractères)');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.post(
        `${API}/api/properties/parse-mandate`,
        { text },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setExtracted(data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Erreur lors de l\'extraction IA');
    } finally {
      setLoading(false);
    }
  };

  const apply = () => {
    if (!extracted) return;
    onApply(extracted);
    close();
  };

  const meta = extracted?.extracted_metadata || {};
  const nc = extracted?.nouveau_contact || {};

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4" data-testid="import-mandate-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[92vh] overflow-hidden flex flex-col">

        {/* Header */}
        <div className="bg-gradient-to-r from-violet-600 to-indigo-700 text-white px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              <i className="bi bi-stars text-xl"></i>
            </div>
            <div>
              <h2 className="text-lg font-bold">Importer depuis un mandat (IA)</h2>
              <p className="text-xs opacity-90">Collez le texte du mandat — l'IA Komerces extrait automatiquement les champs</p>
            </div>
          </div>
          <button
            onClick={close}
            className="text-white/80 hover:text-white p-2 hover:bg-white/10 rounded-lg transition"
            data-testid="import-mandate-close"
          >
            <i className="bi bi-x-lg text-xl"></i>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {!extracted ? (
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-semibold text-slate-700">Texte du mandat</label>
                  <button
                    type="button"
                    onClick={() => setText(SAMPLE_MANDATE)}
                    className="text-xs text-violet-600 hover:text-violet-800 font-medium"
                    data-testid="import-mandate-sample"
                  >
                    <i className="bi bi-lightning-charge"></i> Insérer un exemple
                  </button>
                </div>
                <textarea
                  data-testid="import-mandate-textarea"
                  value={text}
                  onChange={(e) => { setText(e.target.value); setError(null); }}
                  rows={14}
                  placeholder="Collez ici le contenu du mandat (issu d'un PDF, Word ou email — minimum 50 caractères)..."
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm font-mono leading-relaxed resize-none"
                />
                <div className="flex justify-between mt-1.5 text-xs text-slate-500">
                  <span>{text.length} caractères</span>
                  <span>L'IA extrait : opération, type de bien, prix, loyer, surface, propriétaire, mandat, dates…</span>
                </div>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-lg text-sm" data-testid="import-mandate-error">
                  <i className="bi bi-exclamation-triangle mr-2"></i>{error}
                </div>
              )}

              <button
                onClick={analyze}
                disabled={loading || text.trim().length < 50}
                className="w-full py-3 bg-violet-600 hover:bg-violet-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
                data-testid="import-mandate-analyze-btn"
              >
                <i className={`bi ${loading ? 'bi-arrow-repeat animate-spin' : 'bi-magic'}`}></i>
                {loading ? 'Analyse IA en cours… (~10s)' : 'Analyser le mandat avec l\'IA'}
              </button>
            </div>
          ) : (
            <div className="space-y-4" data-testid="import-mandate-preview">
              {/* Confidence banner */}
              <div className={`flex items-center gap-3 p-3 rounded-lg border ${
                meta.confidence === 'high' ? 'bg-emerald-50 border-emerald-200' :
                meta.confidence === 'low' ? 'bg-amber-50 border-amber-200' :
                'bg-slate-50 border-slate-200'
              }`}>
                <i className={`bi ${meta.confidence === 'high' ? 'bi-check-circle-fill text-emerald-600' : 'bi-info-circle-fill text-amber-600'} text-xl`}></i>
                <div className="flex-1 text-sm">
                  <p className="font-semibold text-slate-800">
                    {meta.fields_found || 0} champs extraits — Confiance : {meta.confidence || 'medium'}
                  </p>
                  {meta.notes && <p className="text-xs text-slate-600 mt-0.5">{meta.notes}</p>}
                </div>
              </div>

              {/* Grid 2 colonnes : Bien | Propriétaire */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                  <h3 className="text-xs font-bold uppercase text-slate-500 mb-3 flex items-center gap-2">
                    <i className="bi bi-building text-violet-600"></i> Bien
                  </h3>
                  <FieldRow label="Titre" value={extracted.titre_annonce} isImportant />
                  <FieldRow label="Opération" value={extracted.operation?.replace(/_/g, ' ')} />
                  <FieldRow label="Objet" value={extracted.objet?.replace(/_/g, ' ')} />
                  <FieldRow label="Profil" value={extracted.profil} />
                  <FieldRow label="Sous-profil" value={extracted.sous_profil} />
                  <FieldRow label="Adresse" value={extracted.adresse} />
                  <FieldRow label="Ville" value={extracted.ville} />
                  <FieldRow label="Code postal" value={extracted.code_postal} />
                  <FieldRow label="Surface" value={extracted.surface_totale_m2 ? formatSurfaceFR(extracted.surface_totale_m2) : null} />
                  <FieldRow label="Prix net vendeur" value={extracted.prix ? formatPriceFR(extracted.prix) : null} isImportant />
                  <FieldRow label="Pas de porte / Droit d'entrée" value={extracted.droit_entree ? formatPriceFR(extracted.droit_entree) : null} isImportant />
                  <FieldRow label="Loyer mensuel" value={(extracted.loyer_mensuel || extracted.loyer) ? formatRentFR(extracted.loyer_mensuel || extracted.loyer) : null} isImportant />
                  <FieldRow label="Charges" value={extracted.charges ? `${extracted.charges} €` : null} />
                  <FieldRow label="Honoraires (€ TTC)" value={extracted.honoraires ? formatPriceFR(extracted.honoraires) : null} />
                  <FieldRow label="Honoraires (% du net vendeur)" value={extracted.honoraires_pct ? `${extracted.honoraires_pct} %` : null} />
                  <FieldRow label="Prix FAI (calculé)" value={
                    (extracted.prix && (extracted.honoraires || (extracted.honoraires_pct && extracted.prix)))
                      ? formatPriceFR(Math.round(Number(extracted.prix) + (Number(extracted.honoraires) || (Number(extracted.prix) * Number(extracted.honoraires_pct)) / 100)))
                      : null
                  } />
                  <FieldRow label="Honoraires à la charge de" value={
                    extracted.honoraires_charge === 'vendeur' ? 'Vendeur / Bailleur' :
                    extracted.honoraires_charge === 'acquereur' ? 'Acquéreur / Preneur' : null
                  } />
                  <FieldRow label="CA HT" value={extracted.ca_ht ? formatPriceFR(extracted.ca_ht) : null} />
                  <FieldRow label="EBE retraité" value={extracted.ebe_retraite ? formatPriceFR(extracted.ebe_retraite) : null} />
                </div>

                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                  <h3 className="text-xs font-bold uppercase text-slate-500 mb-3 flex items-center gap-2">
                    <i className="bi bi-person text-emerald-600"></i> Propriétaire
                  </h3>
                  <FieldRow label="Civilité" value={nc.civilite} />
                  <FieldRow label="Nom" value={nc.nom} isImportant />
                  <FieldRow label="Prénom" value={nc.prenom} isImportant />
                  <FieldRow label="Type" value={nc.type} />
                  {nc.raison_sociale && <FieldRow label="Société" value={nc.raison_sociale} />}
                  {nc.siret && <FieldRow label="SIRET" value={nc.siret} />}
                  <FieldRow label="Téléphone" value={nc.telephone} isImportant />
                  <FieldRow label="Email" value={nc.email} isImportant />
                  <FieldRow label="Adresse" value={nc.adresse} />
                  <FieldRow label="Ville" value={nc.ville} />
                  <FieldRow label="Code postal" value={nc.code_postal} />

                  <h3 className="text-xs font-bold uppercase text-slate-500 mt-4 mb-3 flex items-center gap-2">
                    <i className="bi bi-file-text text-blue-600"></i> Mandat
                  </h3>
                  <FieldRow label="N° mandat" value={extracted.numero_mandat} />
                  <FieldRow label="Date" value={extracted.date_mandat} />
                  <FieldRow label="Durée" value={extracted.duree_mandat_mois ? `${extracted.duree_mandat_mois} mois` : null} />
                  <FieldRow label="Exclusivité" value={extracted.exclusivite} />
                </div>
              </div>

              {/* Description */}
              {extracted.description_courte && (
                <div className="bg-violet-50 border-l-4 border-violet-500 rounded-r-lg p-3">
                  <p className="text-[10px] font-bold uppercase text-violet-700 mb-1">Description suggérée</p>
                  <p className="text-sm text-slate-700 leading-snug">{extracted.description_courte}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {extracted && (
          <div className="border-t border-slate-200 px-6 py-4 flex items-center justify-between gap-3 bg-slate-50">
            <button
              onClick={() => setExtracted(null)}
              className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-200 rounded-lg transition-colors"
              data-testid="import-mandate-back-btn"
            >
              <i className="bi bi-arrow-left mr-1.5"></i>Modifier le texte
            </button>
            <button
              onClick={apply}
              className="px-6 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg transition-colors flex items-center gap-2 shadow-md"
              data-testid="import-mandate-apply-btn"
            >
              <i className="bi bi-check2-circle"></i>Pré-remplir le wizard
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ImportMandateModal;
